import { useState, useRef, useEffect } from 'react';
import { Upload, FileText, Loader2, Trash2, CheckCircle, AlertTriangle, Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface AttestationRow {
  "Ancien code eclosion": string;
  "Num_Parent_QS": string;
  "Num enfant QS": string;
  "Matricule QS": string;
  "Client": string;
  "code_enfant_compta_dbl": string;
  "User_id_Supa": string;
  "École": string;
  "Classe": string;
  "email": string;
  "Numéro de l'attestation": string;
  "Numéro national Enfant": string;
  "Nom enfant": string;
  "Prénom enfant": string;
  "Date de naissance": string;
  "Rue enfant": string;
  "Code postal enfant": string;
  "Commune enfant": string;
  "Code pays enfant": string;
  "Numéro national": string;
  "Nom": string;
  "Prénom": string;
  "Vide": string;
  "Rue": string;
  "Code postal": string;
  "Commune": string;
  "Code pays": string;
  "Date début": string;
  "Date fin": string;
  "Nb jour(s)": string;
  "Tarif Jour": string;
  "Montant": string;
  "Id PDF": string;
  "Imprimé": string;
  "Envoyé": string;
  "date d'envoie": string;
  year?: number;
}

const CSV_HEADER_MAP: Record<string, string> = {
  'Ancien code eclosion': 'Ancien code eclosion',
  'Num_Parent_QS': 'Num_Parent_QS',
  'Num enfant QS': 'Num enfant QS',
  'Matricule QS': 'Matricule QS',
  'Client': 'Client',
  'code_enfant_compta_dbl': 'code_enfant_compta_dbl',
  'User_id_Supa': 'User_id_Supa',
  'École': 'École',
  'Classe': 'Classe',
  'email': 'email',
  "Numéro de l'attestation": "Numéro de l'attestation",
  'Numéro national Enfant': 'Numéro national Enfant',
  'Nom enfant': 'Nom enfant',
  'Prénom enfant': 'Prénom enfant',
  'Date de naissance': 'Date de naissance',
  'Rue': 'Rue enfant',
  'Code postal': 'Code postal enfant',
  'Commune/ville': 'Commune enfant',
  'Code pays': 'Code pays enfant',
  'Numéro national': 'Numéro national',
  'Nom': 'Nom',
  'Prénom': 'Prénom',
  'Vide': 'Vide',
  'Date début': 'Date début',
  'Date fin': 'Date fin',
  'Nb jour(s)': 'Nb jour(s)',
  'Tarif Jour': 'Tarif Jour',
  'Montant': 'Montant',
  'Id PDF': 'Id PDF',
  'Imprimé': 'Imprimé',
  'Envoyé': 'Envoyé',
  "date d'envoie": "date d'envoie",
};

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

function normalizeHeader(header: string): string {
  return header
    .replace(/\ufeff/g, '')
    .replace(/Ã©/g, 'é')
    .replace(/Ã‰/g, 'É')
    .replace(/Ã¨/g, 'è')
    .replace(/Ã /g, 'à')
    .replace(/Ã¢/g, 'â')
    .replace(/Ã´/g, 'ô')
    .replace(/Ã®/g, 'î')
    .replace(/Ã¹/g, 'ù')
    .replace(/Ã§/g, 'ç')
    .replace(/Ã«/g, 'ë')
    .replace(/Ã¯/g, 'ï')
    .replace(/Ãª/g, 'ê')
    .replace(/Ã¼/g, 'ü')
    .trim();
}

function extractYear(attestationNumber: string): number | null {
  const match = attestationNumber.match(/-(\d+)$/);
  if (match) {
    return 2000 + parseInt(match[1], 10);
  }
  return null;
}

const BATCH_SIZE = 200;

const AdminAttestationsPage = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [isLoadingCount, setIsLoadingCount] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewData, setPreviewData] = useState<{ headers: string[]; rows: string[][]; totalRows: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchCount();
  }, []);

  const fetchCount = async () => {
    setIsLoadingCount(true);
    const { count, error } = await supabase
      .from('Attestations fiscales')
      .select('id', { count: 'exact', head: true });
    if (!error && count !== null) {
      setRowCount(count);
    }
    setIsLoadingCount(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) {
        toast.error('Le fichier CSV est vide ou ne contient pas de données.');
        return;
      }

      const headers = parseCSVLine(lines[0]).map(normalizeHeader);
      const previewRows = lines.slice(1, 6).map(l => parseCSVLine(l));
      setPreviewData({ headers, rows: previewRows, totalRows: lines.length - 1 });
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleImport = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress({ current: 0, total: 0 });

    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      const rawHeaders = parseCSVLine(lines[0]).map(normalizeHeader);
      const dataLines = lines.slice(1);
      const total = dataLines.length;
      setUploadProgress({ current: 0, total });

      // Map CSV headers to DB columns
      // The CSV has duplicate column names (Rue, Code postal, Commune/ville, Code pays appear twice)
      // First occurrence = child, second occurrence = parent
      const dbColumns: string[] = [];
      const seenDuplicates: Record<string, number> = {};

      for (const header of rawHeaders) {
        const count = (seenDuplicates[header] || 0) + 1;
        seenDuplicates[header] = count;

        if (header === 'Rue' && count === 1) {
          dbColumns.push('Rue enfant');
        } else if (header === 'Rue' && count === 2) {
          dbColumns.push('Rue');
        } else if (header === 'Code postal' && count === 1) {
          dbColumns.push('Code postal enfant');
        } else if (header === 'Code postal' && count === 2) {
          dbColumns.push('Code postal');
        } else if ((header === 'Commune/ville' || header === 'Commune') && count === 1) {
          dbColumns.push('Commune enfant');
        } else if ((header === 'Commune/ville' || header === 'Commune') && count === 2) {
          dbColumns.push('Commune');
        } else if (header === 'Code pays' && count === 1) {
          dbColumns.push('Code pays enfant');
        } else if (header === 'Code pays' && count === 2) {
          dbColumns.push('Code pays');
        } else if (header === 'Numéro national' && count === 1) {
          dbColumns.push('Numéro national');
        } else if (header === 'Nom' && count === 1) {
          dbColumns.push('Nom');
        } else if (header === 'Prénom' && count === 1) {
          dbColumns.push('Prénom');
        } else {
          dbColumns.push(CSV_HEADER_MAP[header] || header);
        }
      }

      // Process in batches
      let inserted = 0;
      for (let i = 0; i < total; i += BATCH_SIZE) {
        const batch = dataLines.slice(i, i + BATCH_SIZE);
        const rows: Record<string, any>[] = [];

        for (const line of batch) {
          if (!line.trim()) continue;
          const values = parseCSVLine(line);
          const row: Record<string, any> = {};

          for (let j = 0; j < dbColumns.length && j < values.length; j++) {
            const col = dbColumns[j];
            if (col) {
              row[col] = values[j] || null;
            }
          }

          // Calculate year from attestation number
          const attNum = row["Numéro de l'attestation"];
          if (attNum) {
            row.year = extractYear(attNum);
          }

          rows.push(row);
        }

        if (rows.length > 0) {
          const { error } = await supabase
            .from('Attestations fiscales')
            .insert(rows);

          if (error) {
            console.error('Batch insert error:', error);
            toast.error(`Erreur lors de l'import (batch ${Math.floor(i / BATCH_SIZE) + 1}): ${error.message}`);
            setIsUploading(false);
            return;
          }
          inserted += rows.length;
        }

        setUploadProgress({ current: Math.min(inserted, total), total });
      }

      toast.success(`Import terminé : ${inserted} lignes importées.`);
      setPreviewData(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchCount();
    } catch (err: any) {
      console.error('Import error:', err);
      toast.error(`Erreur: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer TOUTES les attestations fiscales ? Cette action est irréversible.')) {
      return;
    }

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('Attestations fiscales')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');

      if (error) throw error;
      toast.success('Toutes les attestations ont été supprimées.');
      setRowCount(0);
    } catch (err: any) {
      toast.error(`Erreur: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attestations fiscales</h1>
          <p className="text-gray-500 mt-1">Import et gestion des attestations fiscales par CSV</p>
        </div>
        <div className="flex items-center gap-3">
          {isLoadingCount ? (
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          ) : (
            <span className="text-sm bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full font-medium">
              {rowCount} attestation{(rowCount || 0) > 1 ? 's' : ''} en base
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total attestations</p>
              <p className="text-xl font-bold text-gray-900">{rowCount ?? '-'}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Statut</p>
              <p className="text-xl font-bold text-gray-900">
                {(rowCount || 0) > 0 ? 'Données présentes' : 'Vide'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Actions</p>
              <button
                onClick={handleDeleteAll}
                disabled={isDeleting || rowCount === 0}
                className="text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'Suppression...' : 'Tout supprimer'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Upload section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Importer un fichier CSV</h2>
        <p className="text-sm text-gray-500 mb-4">
          Le fichier CSV doit contenir les en-têtes standards des attestations fiscales.
          L'import se fait par lots de {BATCH_SIZE} lignes pour éviter les timeouts.
        </p>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors">
          <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">Glissez votre fichier CSV ici ou cliquez pour sélectionner</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
            id="csv-upload"
          />
          <label
            htmlFor="csv-upload"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <Upload className="h-4 w-4 mr-2" />
            Choisir un fichier
          </label>
        </div>
      </div>

      {/* Preview */}
      {previewData && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Aperçu du fichier</h2>
              <p className="text-sm text-gray-500">
                {previewData.totalRows} lignes détectées - {previewData.headers.length} colonnes
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setPreviewData(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleImport}
                disabled={isUploading}
                className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Lancer l'import ({previewData.totalRows} lignes)
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Progress bar */}
          {isUploading && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Progression</span>
                <span>{uploadProgress.current} / {uploadProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-green-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress.total ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {/* Headers preview */}
          <div className="mb-3">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Colonnes détectées :</h3>
            <div className="flex flex-wrap gap-1.5">
              {previewData.headers.map((h, i) => (
                <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  {h}
                </span>
              ))}
            </div>
          </div>

          {/* Data preview table */}
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  {previewData.headers.slice(0, 10).map((h, i) => (
                    <th key={i} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                  {previewData.headers.length > 10 && (
                    <th className="px-3 py-2 text-left font-medium text-gray-400">
                      +{previewData.headers.length - 10} colonnes
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {previewData.rows.map((row, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    {row.slice(0, 10).map((cell, j) => (
                      <td key={j} className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-[150px] truncate">
                        {cell || '-'}
                      </td>
                    ))}
                    {previewData.headers.length > 10 && (
                      <td className="px-3 py-2 text-gray-400">...</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-800 font-medium">Conseils d'import</p>
            <ul className="text-amber-700 text-sm mt-1 list-disc list-inside space-y-1">
              <li>Assurez-vous que le fichier est encodé en UTF-8</li>
              <li>Le séparateur doit être la virgule (,)</li>
              <li>Si vous réimportez des données, supprimez d'abord les anciennes avec "Tout supprimer"</li>
              <li>La colonne "year" est calculée automatiquement depuis le numéro d'attestation</li>
              <li>Les colonnes dupliquées (Rue, Code postal, Commune, Code pays) sont mappées : 1re occurrence = enfant, 2e = parent</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAttestationsPage;
